import React from 'react';
import { CheckCircleFilled, DownOutlined, FolderOpenOutlined, HomeOutlined, MoreOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Empty, Flex, Modal, Dropdown, Pagination, Space, Tag, theme, Typography } from 'antd';

const { Text, Title } = Typography;
const PAGE_SIZE = 40;

const Desktop = ({ viewModel }) => {
  const { token } = theme.useToken();

  const footer = (
    <Flex justify='space-between' align='center' style={{ width: '100%' }} gap={12}>
      <Flex align='center' gap={8} wrap='wrap'>
        <Tag color={viewModel.selectedCount > 0 ? 'blue' : 'default'}>{viewModel.selectedCount} ảnh đang chọn</Tag>
        <Text type='secondary'>Có thể chọn thêm {viewModel.remainingSlots} / {viewModel.maxPhotos} ảnh</Text>
      </Flex>

      <Space>
        <Button onClick={viewModel.onCancel}>Đóng</Button>
        <Button type='primary' onClick={viewModel.confirmSelection} disabled={viewModel.selectedCount === 0}>Thêm ảnh đã chọn</Button>
      </Space>
    </Flex>
  );

  return (
    <Modal
      title={
        <Flex vertical gap={2}>
          <Title level={5} style={{ margin: 0 }}>Chọn hình ảnh</Title>
          <Text type='secondary'>Chọn từ thư viện hoặc tải ảnh mới lên thư mục hiện tại</Text>
        </Flex>
      }
      open={viewModel.open}
      onCancel={viewModel.onCancel}
      width='80vw'
      footer={footer}
    >
      <input type='file' accept='image/*' multiple onChange={viewModel.onFileChange} ref={viewModel.fileInputRef} style={{ display: 'none' }} />

      <Flex vertical gap={16}>
        <Flex justify='space-between' align='flex-start' gap={12} wrap='wrap'>
          <Flex vertical gap={8} style={{ flex: 1, minWidth: 320 }}>
            <Flex align='center' gap={6} wrap='wrap'>
              {viewModel.breadcrumbFolders.map((item, index) => (
                <Space key={item.id} size={4}>
                  <Button
                    type='link'
                    size='small'
                    icon={index === 0 ? <HomeOutlined /> : null}
                    style={{ paddingInline: 0 }}
                    onClick={() => viewModel.openFolder(item.id)}
                  >
                    {item.name}
                  </Button>
                  {index < viewModel.breadcrumbFolders.length - 1 ? <Text type='secondary'>/</Text> : null}
                </Space>
              ))}
            </Flex>

            <Flex gap={8} wrap='wrap'>
              {viewModel.childFolders.map((item) => (
                <Button key={item.id} icon={<FolderOpenOutlined />} onClick={() => viewModel.openFolder(item.id)}>
                  {item.name}
                </Button>
              ))}
              {viewModel.childFolders.length === 0 ? <Text type='secondary'>Thư mục hiện tại chưa có thư mục con.</Text> : null}
            </Flex>
          </Flex>

          <Space wrap>
            {viewModel.currentFolder ? (
              <Dropdown menu={{ onClick: viewModel.onFolderActionClick, items: viewModel.folderActions }} trigger={['click']}>
                <Button icon={<MoreOutlined />}>Thư mục</Button>
              </Dropdown>
            ) : null}
            <Button icon={<PlusOutlined />} onClick={() => viewModel.setShowCreateFolder(true)}>Tạo folder</Button>
            <Button icon={<UploadOutlined />} onClick={() => viewModel.fileInputRef.current?.click()} loading={viewModel.uploading}>Tải ảnh lên</Button>
          </Space>
        </Flex>

        <Flex justify='space-between' align='center' gap={12} wrap='wrap'>
          <Flex align='center' gap={8} wrap='wrap'>
            <Tag color={viewModel.selectedCount > 0 ? 'blue' : 'default'}>{viewModel.selectedCount} ảnh đã chọn</Tag>
            <Text type='secondary'>Kho hiện tại có {viewModel.totalFiles} ảnh</Text>
          </Flex>

          <Space wrap>
            {viewModel.selectedCount > 0 ? (
              <Dropdown menu={{ items: viewModel.bulkActionItems, onClick: viewModel.onBulkActionClick }}>
                <Button icon={<DownOutlined />}>Tác vụ cho ảnh đã chọn</Button>
              </Dropdown>
            ) : null}
            <Button onClick={viewModel.clearSelection} disabled={viewModel.selectedCount === 0}>Bỏ chọn</Button>
          </Space>
        </Flex>

        <div style={{ minHeight: 420, maxHeight: '56vh', overflowY: 'auto', paddingRight: 4 }}>
          {viewModel.pagedFiles.length === 0 ? (
            <Empty description='Thư mục này chưa có ảnh nào' style={{ marginBlock: 64 }} />
          ) : (
            <Flex gap={16} wrap='wrap' align='flex-start'>
              {viewModel.pagedFiles.map((file) => {
                const isSelected = Boolean(file.selected);

                return (
                  <Badge
                    key={file.id}
                    count={isSelected ? <CheckCircleFilled style={{ color: token.colorPrimary, fontSize: 18 }} /> : 0}
                    offset={[-8, 8]}
                  >
                    <Card
                      hoverable
                      size='small'
                      onClick={() => viewModel.toggleImageSelected(file)}
                      style={{
                        width: 152,
                        borderColor: isSelected ? token.colorPrimary : token.colorBorderSecondary,
                        boxShadow: isSelected ? `0 0 0 2px ${token.colorPrimaryBorder}` : 'none',
                      }}
                      styles={{ body: { padding: 8 } }}
                      cover={
                        <div style={{ height: 120, overflow: 'hidden', background: token.colorFillAlter }}>
                          <img src={file.sm_url || file.url} alt={file.name || 'image'} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        </div>
                      }
                    >
                      <Flex vertical gap={2}>
                        <Text ellipsis={{ tooltip: file.name || file.url }}>{file.name || `Ảnh #${file.id}`}</Text>
                        <Text type='secondary' style={{ fontSize: 12 }}>{isSelected ? 'Đã chọn' : 'Nhấn để chọn'}</Text>
                      </Flex>
                    </Card>
                  </Badge>
                );
              })}
            </Flex>
          )}
        </div>

        <Flex justify='end'>
          <Pagination
            current={viewModel.currentPage}
            pageSize={PAGE_SIZE}
            total={viewModel.totalFiles}
            onChange={(page) => viewModel.setCurrentPage(page)}
            showSizeChanger={false}
            size='small'
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default Desktop;
